import { useDownloadBill, useViewBill } from "@features/bx/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSingleBillDraftList } from "../hooks";

type SortField =
  | "consumer"
  | "billCycle"
  | "billingMonth"
  | "consumption"
  | "billedAmount"
  | "generatedDate";
type SortDirection = "asc" | "desc";

const getStatusBadgeConfig = (status: SingleBill["status"]) => {
  switch (status) {
    case "paid":
      return {
        variant: "default" as const,
        className: "bg-green-100 text-green-800",
      };
    case "delivered":
      return {
        variant: "secondary" as const,
        className: "bg-blue-100 text-blue-800",
      };
    case "overdue":
      return {
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800",
      };
    case "draft":
      return {
        variant: "outline" as const,
        className: "bg-yellow-100 text-yellow-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const SingleBillDraft = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filters, setFilters] = useState<SingleBillFiltersType>({
    status: "all",
    billCycle: "all",
    billingMonth: "all",
    dateRange: {
      from: null,
      to: null,
    },
  });

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();
  const CURRENCY = useCurrency();

  const { data: singleDraftBills } = useSingleBillDraftList(page);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
  };

  const handleFiltersChange = (newFilters: SingleBillFiltersType) => {
    setFilters(newFilters);
  };

  const navigate = useNavigate();

  const clearFilters = () => {
    setFilters({
      status: "all",
      billCycle: "all",
      billingMonth: "all",
      dateRange: {
        from: null,
        to: null,
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const steps = [
    "consumer-selection",
    "account-overview",
    "generate-bill",
    "generate-bill",
  ];

  const handleEdit = async (bill: any) => {
    const selectedStep = steps[bill?.step?.stepWorkingOn];

    if (selectedStep) {
      if (selectedStep === "generate-bill") {
        navigate(
          `/bx/single-bill-generation/${bill?.billDraftData?.billId}/${bill.id}/draft#${selectedStep}`
        );
      } else {
        navigate(
          `/bx/single-bill-generation/${bill?.id}/draft#${selectedStep}`
        );
      }
    }
  };

  // Define columns for the generic table
const columns: TableColumn[] = [
  {
    key: "billDraftData",
    header: "Consumer No",
    type: "text",
    render: (value: any) =>
      value?.consumerData?.consumerNo ?? "NA",
  },
  {
    key: "billDraftData",
    header: "Consumer Name",
    type: "text",
    render: (value: any) => {
      const first = value?.consumerData?.firstName ?? "";
      const last = value?.consumerData?.lastName ?? "";
      return first || last ? `${first} ${last}`.trim() : "NA";
    },
  },
  {
    key: "billDraftData",
    header: "Utility Service",
    type: "text",
    render: (value: any) =>
      value?.readingData
        ?.map((r: any) => r.productCode)
        .filter(Boolean)
        .join(", ") || "NA",
  },
  {
    key: "step",
    header: "Step Working On",
    type: "text",
    render: (value: any) => {
      const step = value?.stepWorkingOn;
      return typeof step === "number" ? step + 1 : "NA";
    },
  },
  {
    key: "statusDisplay",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) =>
        getStatusBadgeConfig(value as SingleBill["status"]).variant,
      getClassName: (value: string) =>
        getStatusBadgeConfig(value as SingleBill["status"]).className,
    },
  },
];


  // Define actions for the generic table
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: (bill) => {
        console.log(bill);
        handleEdit(bill);
      },
      icon: "Pen",
      variant: "outline",
    },
  ];

  return (
    <div className="space-y-4">
      <GenericTable
        data={singleDraftBills?.result ?? []}
        columns={columns}
        loading={false}
        rowKey="id"
        pagination={{
          currentPage: page,
          totalPages: Math.ceil((singleDraftBills?.count || 0) / PAGE_SIZE),
          totalItems: singleDraftBills?.count || 0,
          itemsPerPage: PAGE_SIZE,
          startItem: (page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(page * PAGE_SIZE, singleDraftBills?.count || 0),
        }}
        onPageChange={handlePageChange}
        sortConfig={
          sortField && sortDirection
            ? { field: sortField, direction: sortDirection }
            : undefined
        }
        onSort={handleSort}
        actions={actions}
        actionsType="icons"
        emptyMessage="No bills found"
      />
    </div>
  );
};
